import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Inbox, FileUp, Users, BookOpen, LogOut, ExternalLink } from "lucide-react";
import { useAdmin } from "./AdminContext";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
    isActive ? "bg-white/10 text-ivory" : "text-ivory/60 hover:text-ivory hover:bg-white/5"
  }`;

export function AdminLayout({ children }) {
  const { user, logout } = useAdmin();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate("/admin/login");
  };

  const items = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/listings", label: "Properties", icon: Building2 },
    { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
    { to: "/admin/import", label: "Import CSV", icon: FileUp },
    ...(user?.role === "owner" ? [{ to: "/admin/team", label: "Team & Access", icon: Users }] : []),
    { to: "/admin/guide", label: "User Guide", icon: BookOpen },
  ];

  return (
    <div className="min-h-screen bg-charcoal text-ivory" data-testid="admin-layout">
      <header className="sticky top-0 z-40 border-b border-ivory/10 bg-charcoal/95 backdrop-blur">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="PNG Landed Excellence" className="h-8 w-auto" />
            <span className="hidden text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-ivory/50 sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site" className="hidden items-center gap-1.5 text-xs text-ivory/60 hover:text-ivory sm:flex">
              View website <ExternalLink className="h-3 w-3" />
            </a>
            <div className="text-right">
              <p className="text-sm font-medium leading-tight" data-testid="admin-user-name">{user?.name}</p>
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ivory/50">{user?.role === "owner" ? "Owner" : "Property Manager"}</p>
            </div>
            <button onClick={signOut} data-testid="admin-logout" className="flex items-center gap-2 border border-ivory/20 px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/80 hover:bg-white/10">
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-ivory/10 px-3 lg:hidden" aria-label="Admin">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} className={linkCls} data-testid={`admin-nav-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <i.icon className="h-4 w-4" /> {i.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <div className="mx-auto flex max-w-[1400px]">
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-56 shrink-0 border-r border-ivory/10 py-6 pr-2 lg:block">
          <nav className="flex flex-col gap-1" aria-label="Admin">
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} className={linkCls} data-testid={`admin-nav-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <i.icon className="h-4 w-4" /> {i.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1 px-4 py-8 sm:px-8">{children}</main>
      </div>
    </div>
  );
}
